<template>
  <article class="gcard">
    <div class="gcard__body">
      <div class="gcard__head">
        <div class="gcard__id">
          <div class="gcard__eyebrow">{{ $t("groupCard.eyebrow") }}</div>
          <div class="gcard__name">
            {{ $t("groupCard.name", { code: item.cod }) }}
          </div>
        </div>
        <span class="gcard__spots">
          {{
            $tc("groupCard.spots", item.availableSpots, {
              count: item.availableSpots,
            })
          }}
        </span>
      </div>

      <div class="gcard__teacher">
        <span class="gcard__avatar">{{ teacherInitials }}</span>
        <div>
          <div class="gcard__k">{{ $t("groupCard.teacher") }}</div>
          <div class="gcard__teacher-name">{{ item.teacher?.name || "-" }}</div>
        </div>
      </div>

      <div class="gcard__facts">
        <div class="gcard__fact">
          <span class="gcard__k">{{ $t("groupCard.level") }}</span>
          <span class="gcard__v">{{ displayGroupLevel(item.level) }}</span>
        </div>
        <div class="gcard__fact">
          <span class="gcard__k">{{ $t("groupCard.format") }}</span>
          <span class="gcard__v">{{ displayGroupFormat(item.format) }}</span>
        </div>
        <div class="gcard__fact">
          <span class="gcard__k">{{ $t("groupCard.frequency") }}</span>
          <span class="gcard__v">{{
            displayGroupFrequency(item.frequency)
          }}</span>
        </div>
        <div class="gcard__fact">
          <span class="gcard__k">{{ $t("groupCard.activeStudents") }}</span>
          <span class="gcard__v"
            >{{ item.studentsCount }} / {{ item.capacity }}</span
          >
        </div>
      </div>

      <div class="gcard__block">
        <div class="gcard__block-label">{{ $t("groupCard.schedule") }}</div>
        <div v-if="normalizedTimes.length" class="gcard__chips">
          <span
            v-for="slot in normalizedTimes"
            :key="`${slot.weekDay}-${slot.start}-${slot.end}`"
            class="gcard__chip"
          >
            {{ weekDayLabel(slot.weekDay) }} · {{ slot.start }}–{{ slot.end }}
          </span>
        </div>
        <span v-else class="gcard__muted">
          {{ $t("groupCard.noSchedule") }}
        </span>
      </div>

      <!-- Quem já estuda aqui: o candidato decide sabendo com quem vai dividir
           a turma, não só o formato e o horário. -->
      <div class="gcard__block">
        <div class="gcard__block-label">{{ $t("groupCard.mates") }}</div>
        <div v-if="classmates.length" class="gcard__mates">
          <div v-for="mate in classmates" :key="mate.id" class="gcard__mate">
            <span class="gcard__mate-avatar">{{ initials(mate.name) }}</span>
            <div class="gcard__mate-text">
              <span class="gcard__mate-name">{{ mate.name }}</span>
              <span v-if="mate.bio" class="gcard__mate-bio">{{
                mate.bio
              }}</span>
              <span v-else class="gcard__mate-bio gcard__mate-bio--empty">{{
                $t("groupCard.noBio")
              }}</span>
            </div>
          </div>
        </div>
        <span v-else class="gcard__muted">
          {{ $t("groupCard.first") }}
        </span>
      </div>
    </div>

    <div class="gcard__foot">
      <!-- Card 86ajbxjn7: o card da turma também informa o valor. É o plano
           do professor para a modalidade e a frequência desta turma. -->
      <div class="gcard__price">
        <span class="gcard__k">{{ $t("groupCard.fee") }}</span>
        <span class="gcard__price-value">{{ priceLabel }}</span>
      </div>
      <!-- Card 86akp2bkg: uma solicitação por vez. -->
      <button
        type="button"
        class="gcard__btn"
        :disabled="blocked"
        :title="blocked ? $t('common.pendingRequestBlock') : ''"
        @click="confirmOpen = true"
      >
        {{ $t("groupCard.request") }}
      </button>
    </div>

    <!-- Card 86akj7x9f: quem manda é a turma. Antes de enviar o pedido, o
         aluno vê modalidade, frequência e horários dela, com o que diverge do
         que ele queria em destaque, e confirma em "Aceitar e solicitar". -->
    <v-dialog v-model="confirmOpen" max-width="460">
      <v-card class="gconfirm">
        <v-card-title class="gconfirm__title">
          {{ $t("groupCard.confirmTitle", { code: item.cod }) }}
        </v-card-title>

        <v-card-text>
          <p class="gconfirm__text">
            {{ $t("groupCard.confirmText") }}
          </p>

          <div class="gconfirm__row">
            <span class="gconfirm__k">{{ $t("groupCard.modality") }}</span>
            <span class="gconfirm__v">
              {{ displayGroupFormat(item.format) }}
              <span v-if="formatDiverges" class="gconfirm__tag">
                {{
                  $t("groupCard.wanted", {
                    value: displayGroupFormat(studentFormat),
                  })
                }}
              </span>
            </span>
          </div>

          <div class="gconfirm__row">
            <span class="gconfirm__k">{{ $t("groupCard.frequency") }}</span>
            <span class="gconfirm__v">
              {{ displayGroupFrequency(item.frequency) }}
              <span v-if="frequencyDiverges" class="gconfirm__tag">
                {{
                  $t("groupCard.wanted", {
                    value: displayGroupFrequency(studentFrequency),
                  })
                }}
              </span>
            </span>
          </div>

          <div class="gconfirm__row">
            <span class="gconfirm__k">{{ $t("groupCard.times") }}</span>
            <span class="gconfirm__v">
              <span v-if="!normalizedTimes.length">{{
                $t("groupCard.toBeAgreed")
              }}</span>
              <span
                v-for="(time, index) in normalizedTimes"
                :key="`c-${index}`"
                class="gconfirm__time"
              >
                {{ weekDayLabel(time.weekDay) }} · {{ time.start }}–{{
                  time.end
                }}
              </span>
            </span>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirmOpen = false">{{
            $t("common.cancel")
          }}</v-btn>
          <v-btn color="#E8276F" dark depressed @click="confirmJoin">
            {{ $t("groupCard.accept") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </article>
</template>

<script>
import {
  WEEK_DAY_ITEMS,
  displayGroupFormat,
  displayGroupFrequency,
  displayGroupLevel,
  initialsFromName,
  normalizeClassTimes,
} from "@/shared/classPresentation";
import { formatBRL } from "@/plugins/i18n";

export default {
  name: "MatchedGroupCard",

  props: {
    item: {
      type: Object,
      required: true,
    },
    // O que o aluno pediu no cadastro, para apontar a divergência. Sem isso a
    // confirmação segue valendo, só não destaca o que muda.
    studentFormat: {
      type: [Number, String],
      default: null,
    },
    studentFrequency: {
      type: [Number, String],
      default: null,
    },
    // Card 86akp2bkg: o aluno já tem uma solicitação aguardando resposta.
    blocked: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      confirmOpen: false,
    };
  },

  computed: {
    formatDiverges() {
      return (
        this.studentFormat !== null &&
        +this.studentFormat > 0 &&
        +this.studentFormat !== +this.item.format
      );
    },
    frequencyDiverges() {
      return (
        this.studentFrequency !== null &&
        +this.studentFrequency > 0 &&
        +this.studentFrequency !== +this.item.frequency
      );
    },
    normalizedTimes() {
      return normalizeClassTimes(this.item.weekDays, this.item.hours);
    },
    teacherInitials() {
      return initialsFromName(this.item.teacher?.name || "PE");
    },
    classmates() {
      return Array.isArray(this.item.students) ? this.item.students : [];
    },
    priceLabel() {
      const value = Number(this.item.monthlyValue);

      if (this.item.monthlyValue == null || !(value > 0)) {
        return this.$t("groupCard.toBeAgreed");
      }

      return formatBRL(value);
    },
  },

  methods: {
    displayGroupLevel,
    displayGroupFormat,
    displayGroupFrequency,
    confirmJoin() {
      this.confirmOpen = false;
      this.$emit("requestJoin", this.item);
    },
    initials(name) {
      return initialsFromName(name || "PE");
    },
    weekDayLabel(day) {
      return WEEK_DAY_ITEMS.find((item) => +item.value === +day)?.text || "-";
    },
  },
};
</script>

<style scoped>
.gcard {
  --ink: var(--dk-ink, #231a2e);
  --muted: var(--dk-muted, #6b6472);
  --paper: var(--dk-paper, #fbf7fa);
  --line: rgba(var(--dk-ink-rgb, 35, 26, 46), 0.09);
  --pink: var(--dk-pink, #e8276f);
  height: 100%;
  min-height: 440px;
  display: flex;
  flex-direction: column;
  padding: 22px;
  background: var(--dk-surface, #fff);
  border: 1px solid var(--line);
  border-radius: 15px;
  box-shadow: 0 18px 40px -34px rgba(var(--dk-shadow-rgb, 35, 26, 46), 0.5);
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.gcard:hover {
  transform: translateY(-3px);
  box-shadow: 0 26px 46px -34px rgba(232, 39, 111, 0.5);
}

.gcard__body {
  flex: 1 1 auto;
}

.gcard__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}
.gcard__eyebrow {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--pink);
}
.gcard__name {
  margin-top: 2px;
  font-size: 1.15rem;
  font-weight: 800;
  line-height: 1.2;
  color: var(--ink);
}
.gcard__spots {
  flex: 0 0 auto;
  padding: 4px 11px;
  border-radius: 999px;
  background: rgba(232, 39, 111, 0.1);
  color: var(--dk-pink-deep, #be185d);
  font-size: 0.72rem;
  font-weight: 700;
  white-space: nowrap;
}

.gcard__teacher {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 12px;
  background: var(--paper);
  border: 1px solid var(--line);
}
.gcard__avatar {
  flex: 0 0 auto;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    160deg,
    var(--dk-pink, #ff5595),
    var(--dk-pink, #e8276f)
  );
  color: #fff;
  font-size: 0.82rem;
  font-weight: 800;
}
.gcard__teacher-name {
  margin-top: 2px;
  font-size: 0.98rem;
  font-weight: 700;
  color: var(--ink);
}

.gcard__k,
.gcard__block-label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: var(--muted);
}

.gcard__facts {
  margin-top: 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}
.gcard__fact {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 12px 14px;
  border-radius: 12px;
  background: var(--paper);
  border: 1px solid var(--line);
}
.gcard__v {
  font-size: 0.92rem;
  font-weight: 700;
  color: var(--ink);
}

.gcard__block {
  margin-top: 16px;
}
.gcard__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}
.gcard__chip {
  padding: 4px 11px;
  border-radius: 999px;
  background: rgba(232, 39, 111, 0.08);
  color: var(--dk-pink-deep, #be185d);
  font-size: 0.75rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.gcard__muted {
  display: block;
  margin-top: 8px;
  font-size: 0.82rem;
  color: var(--muted);
}

.gcard__mates {
  display: grid;
  gap: 10px;
  margin-top: 10px;
}
.gcard__mate {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}
.gcard__mate-avatar {
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(232, 39, 111, 0.1);
  color: var(--dk-pink-deep, #be185d);
  font-size: 0.68rem;
  font-weight: 800;
}
.gcard__mate-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.gcard__mate-name {
  font-size: 0.86rem;
  font-weight: 700;
  color: var(--ink);
}
.gcard__mate-bio {
  font-size: 0.78rem;
  line-height: 1.45;
  color: var(--muted);
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.gcard__mate-bio--empty {
  font-style: italic;
}

.gcard__foot {
  margin-top: auto;
  padding-top: 18px;
}
.gcard__price {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 12px;
}
.gcard__price-value {
  font-size: 1.1rem;
  font-weight: 800;
  color: var(--pink);
}
/* confirmação antes de solicitar (card 86akj7x9f) */
.gconfirm__title {
  font-family: "Poppins", sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--ink);
}
.gconfirm__text {
  font-size: 0.85rem;
  color: var(--muted);
}
.gconfirm__row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-top: 1px solid var(--line);
  font-size: 0.86rem;
}
.gconfirm__k {
  color: var(--muted);
}
.gconfirm__v {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  color: var(--ink);
  font-weight: 600;
  text-align: right;
}
.gconfirm__tag {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--dk-ember-deep, #b5421e);
}
.gconfirm__time {
  font-weight: 500;
}

.gcard__btn {
  width: 100%;
  padding: 11px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(
    160deg,
    var(--dk-pink, #e8276f),
    var(--dk-pink-deep, #b51f57)
  );
  color: #fff;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  transition: filter 0.15s ease;
}
.gcard__btn:hover {
  filter: brightness(1.06);
}
.gcard__btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
  filter: none;
}
.gcard__btn:focus-visible {
  outline: 2px solid var(--pink);
  outline-offset: 2px;
}

@media (prefers-reduced-motion: reduce) {
  .gcard {
    transition: none;
  }
  .gcard:hover {
    transform: none;
  }
}

@media (max-width: 400px) {
  .gcard__facts {
    grid-template-columns: 1fr;
  }
}
</style>
